import { createSlice } from "@reduxjs/toolkit";
import {
  fetchWpCars,
  fetchWpCarDetails,
  fetchWpVendors,
} from "../thunks/vehicles.thunks";
import { fetchCurrUser } from "../thunks/users.thunks";
const initialState = {
  user: null,
  data: [],
  originalData: [],
  sorted_data: [],
  originalSorted_data: [],
  carDetails: [],
  favorites: [],
  favorites_data: [],
  vendorDetails: [],
  filters: {
    location: {
      lat: "33.7488",
      lng: "-84.3877",
      radius: 100,
    },
    price_range: {
      min: 0,
      max: 500000,
    },
    year_range: {
      min: 1990,
      max: 2025,
    },
    mile_range: {
      min: 0,
      max: 500000,
    },
    hour_range: {
      min: 0,
      max: 100,
    },
    horsepower_range: {
      min: 0,
      max: 200,
    },
    sizeEngine_range: {
      min: 0,
      max: 1000,
    },
    length_range: {
      min: 0,
      max: 500,
    },
    condition: null,
    extColors: [],
    intColors: [],
    seller: null,
    cleanTitle: null,
  },
  searchTerm: "",
  sortBy: "Closest to Me",
  formId: null,
  isFilter: false,
  isLoading: false,
  isFavoriteLoading: false,
  isError: null,
};

const watercrafts = createSlice({
  name: "watercrafts",
  initialState,
  reducers: {
    setUser(state, action) {
      state.user = action.payload;
    },
    changeLocation(state, action) {
      state.filters.location.lat = action.payload.lat;
      state.filters.location.lng = action.payload.lng;
    },
    changeRadius(state, action) {
      state.filters.location.radius = parseInt(action.payload);
    },
    changePrice(state, action) {
      state.filters.price_range.min = action.payload.min;
      state.filters.price_range.max = action.payload.max;
    },
    changeYear(state, action) {
      state.filters.year_range.min = action.payload.min;
      state.filters.year_range.max = action.payload.max;
    },
    changeHours(state, action) {
      state.filters.hour_range.min = action.payload.min;
      state.filters.hour_range.max = action.payload.max;
    },
    changeLength(state, action) {
      state.filters.length_range.min = action.payload.min;
      state.filters.length_range.max = action.payload.max;
    },
    changeCondition(state, action) {
      state.filters.condition = action.payload;
    },
    changeExtColors(state, action) {
      state.filters.extColors = action.payload;
    },
    changeIntColors(state, action) {
      state.filters.intColors = action.payload;
    },
    changeCleanTitle(state, action) {
      state.filters.cleanTitle = action.payload;
    },
    changeSeller(state, action) {
      state.filters.seller = action.payload;
    },
    flatData(state, action) {
      state.data = state.data.flat();
      state.originalData = [...state.data];
      state.isLoading = false;
    },
    setSortBy(state, action) {
      state.sortBy = action.payload;
      state.searchTerm = "";

      switch (action.payload) {
        case "Closest to Me":
          state.sorted_data = [];
          state.originalSorted_data = [];
          break;

        case "Lowest Mileage":
          const mileageSorted = state.data.sort((a, b) => {
            const aMileage =
              Number(a.meta?.hp_v_details_mileage) || Number(a.miles) || 0; // Use a.meta.hp_v_details_mileage if available, otherwise a.miles
            const bMileage =
              Number(b.meta?.hp_v_details_mileage) || Number(b.miles) || 0; // Use b.meta.hp_v_details_mileage if available, otherwise b.miles

            // Items with no mileage should be pushed to the end
            if (aMileage === 0 && bMileage === 0) return 0; // Both have no mileage, considered equal
            if (aMileage === 0) return 1; // a has no mileage, push a to the end
            if (bMileage === 0) return -1; // b has no mileage, push b to the end

            // Otherwise, compare mileages
            return aMileage - bMileage; // Low to high
          });

          state.sorted_data = mileageSorted;
          state.originalSorted_data = mileageSorted;
          break;

        case "Price (low-high)":
          const priceLowHighSorted = state.data.sort((a, b) => {
            const aPrice = Number(a?.meta?.hp_price || a?.price);
            const bPrice = Number(b?.meta?.hp_price || b?.price);

            if (aPrice === bPrice) {
              return 0;
            } else {
              return aPrice < bPrice ? -1 : 1;
            }
          });

          state.sorted_data = priceLowHighSorted;
          state.originalSorted_data = priceLowHighSorted;
          break;

        case "Price (high-low)":
          const priceHighLowSorted = state.data.sort((a, b) => {
            const aPrice = Number(a?.meta?.hp_price || a.price);
            const bPrice = Number(b?.meta?.hp_price || b.price);

            if (aPrice === bPrice) {
              return 0;
            } else {
              return bPrice - aPrice;
            }
          });
          state.sorted_data = priceHighLowSorted;
          state.originalSorted_data = priceHighLowSorted;
          break;

        case "Recently Posted":
          // Extract and parse dates from each object
          const allDates = state.data.map((item) => ({
            date: new Date(item.date),
            first_seen_at_mc_date: new Date(item.first_seen_at_mc_date),
          }));

          // Flatten the array of dates
          const mergedDates = allDates.reduce((acc, curr) => {
            acc.push(curr.date, curr.first_seen_at_mc_date);
            return acc;
          }, []);

          // Sort merged dates array by most recent date
          mergedDates.sort((a, b) => b.getTime() - a.getTime());

          // Reorder original array based on sorted dates
          const sortedArr = state.data.sort((a, b) => {
            const aDate = new Date(a.date).getTime() || 0;
            const aFirstSeen = new Date(a.first_seen_at_mc_date).getTime() || 0;
            const bDate = new Date(b.date).getTime() || 0;
            const bFirstSeen = new Date(b.first_seen_at_mc_date).getTime() || 0;

            const aCombined = aDate + aFirstSeen;
            const bCombined = bDate + bFirstSeen;

            return bCombined - aCombined; // Sort by most recent combined date
          });

          state.sorted_data = sortedArr;
          state.originalSorted_data = sortedArr;
          break;

        default:
          state.data = state.originalData.slice();
      }
    },
    setSearchTerm(state, action) {
      state.searchTerm = action.payload;
    },
    setDataBySearch(state, action) {
      switch (state.sortBy) {
        case "Price (low-high)":
          const sorted1 = action.payload.sort((a, b) => {
            const aPrice = Number(a?.meta?.hp_price || a?.price);
            const bPrice = Number(b?.meta?.hp_price || b?.price);

            if (aPrice === bPrice) {
              return 0;
            } else {
              return aPrice < bPrice ? -1 : 1;
            }
          });

          if (action.payload.length > 0) {
            state.sorted_data = sorted1;
          } else {
            state.sorted_data = [];
          }
          break;

        case "Price (high-low)":
          const sorted2 = action.payload.sort((a, b) => {
            const aPrice = Number(a?.meta?.hp_price || a.price);
            const bPrice = Number(b?.meta?.hp_price || b.price);

            if (aPrice === bPrice) {
              return 0;
            } else {
              return bPrice - aPrice;
            }
          });

          if (action.payload.length > 0) {
            state.sorted_data = sorted2;
          } else {
            state.sorted_data = [];
          }
          break;

        case "Lowest Mileage":
          const sorted3 = action.payload.sort((a, b) => {
            const aMileage =
              Number(a.meta?.hp_v_details_mileage) || Number(a.miles) || 0; // Use a.meta.hp_v_details_mileage if available, otherwise a.miles
            const bMileage =
              Number(b.meta?.hp_v_details_mileage) || Number(b.miles) || 0; // Use b.meta.hp_v_details_mileage if available, otherwise b.miles

            // Items with no mileage should be pushed to the end
            if (aMileage === 0 && bMileage === 0) return 0; // Both have no mileage, considered equal
            if (aMileage === 0) return 1; // a has no mileage, push a to the end
            if (bMileage === 0) return -1; // b has no mileage, push b to the end

            // Otherwise, compare mileages
            return aMileage - bMileage; // Low to high
          });

          if (action.payload.length > 0) {
            state.sorted_data = sorted3;
          } else {
            state.sorted_data = [];
          }
          break;

        case "Recently Posted":
          // Extract and parse dates from each object
          const allDates = action.payload.map((item) => ({
            date: new Date(item.date),
            first_seen_at_mc_date: new Date(item.first_seen_at_mc_date),
          }));

          // Flatten the array of dates
          const mergedDates = allDates.reduce((acc, curr) => {
            acc.push(curr.date, curr.first_seen_at_mc_date);
            return acc;
          }, []);

          // Sort merged dates array by most recent date
          mergedDates.sort((a, b) => b.getTime() - a.getTime());

          // Reorder original array based on sorted dates
          const sorted4 = action.payload.sort((a, b) => {
            const aDate = new Date(a.date).getTime() || 0;
            const aFirstSeen = new Date(a.first_seen_at_mc_date).getTime() || 0;
            const bDate = new Date(b.date).getTime() || 0;
            const bFirstSeen = new Date(b.first_seen_at_mc_date).getTime() || 0;

            const aCombined = aDate + aFirstSeen;
            const bCombined = bDate + bFirstSeen;

            return bCombined - aCombined; // Sort by most recent combined date
          });
          if (action.payload.length > 0) {
            state.sorted_data = sorted4;
          } else {
            state.sorted_data = [];
          }
          break;

        default:
          state.data = action.payload;
      }
    },
    activateFilter(state, action) {
      state.isFilter = action.payload;
    },
    resetFilter(state, action) {
      state.filters = [];
      state.sorted_data = [];
      state.originalSorted_data = [];
    },
    resetSortedData(state, action) {
      state.sorted_data = [];
      state.originalSorted_data = [];
    },
    reset(state, action) {
      state.data = [];
      state.originalData = [];
      state.sorted_data = [];
      state.originalSorted_data = [];
      state.carDetails = [];
      state.vendorDetails = [];
      state.user = null;
    },
  },
  extraReducers(builder) {
    // FETCH CURRENT USER
    builder.addCase(fetchCurrUser.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(fetchCurrUser.fulfilled, (state, action) => {
      state.user = action.payload;
      state.isLoading = false;
    });
    builder.addCase(fetchCurrUser.rejected, (state, action) => {
      state.isLoading = false;
      console.log(action.error);
    });

    // FETCH WP CARS
    builder.addCase(fetchWpCars.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(fetchWpCars.fulfilled, (state, action) => {
      state.isLoading = true;
      let wpData = [];
      const curPos = state.filters.location;

      // Filter results by location
      const haversineDistance = (coords1, coords2) => {
        const toRad = (x) => (x * Math.PI) / 180;
        const R = 3958.8; // Radius of the Earth in miles

        const dLat = toRad(coords2.lat - coords1.lat);
        const dLng = toRad(coords2.lng - coords1.lng);

        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(toRad(coords1.lat)) *
            Math.cos(toRad(coords2.lat)) *
            Math.sin(dLng / 2) *
            Math.sin(dLng / 2);

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c;
      };

      wpData = action.payload.data.filter((car) => {
        const carCoords = {
          lat: parseFloat(car.meta.hp_latitude),
          lng: parseFloat(car.meta.hp_longitude),
        };
        const distance = haversineDistance(curPos, carCoords);
        return distance <= curPos.radius;
      });

      if (wpData.length === 0) {
        state.isLoading = false;
        return;
      }

      // Filter by Condition
      if (state.filters.condition !== null) {
        let conditionFiltered = [];
        if (state.filters.condition) {
          conditionFiltered = wpData.filter(
            (data) => data?.hp_listing_v_details_condition[0]?.name === "New"
          );
        } else {
          conditionFiltered = wpData;
        }
        wpData = conditionFiltered;
      }

      // Filter by Price
      const priceMin = Number(state.filters.price_range.min);
      const priceMax = Number(state.filters.price_range.max);

      const priceFiltered = wpData.filter(
        (data) =>
          Number(data?.meta?.hp_price) >= priceMin &&
          Number(data?.meta?.hp_price) <= priceMax
      );
      wpData = priceFiltered;

      // Filter by Year
      const yearMin = Number(state.filters.year_range.min);
      const yearMax = Number(state.filters.year_range.max);

      const yearFiltered = wpData.filter(
        (data) =>
          Number(data?.meta?.hp_v_essentials_year) >= yearMin &&
          Number(data?.meta?.hp_v_essentials_year) <= yearMax
      );
      wpData = yearFiltered;

      // Filter by Hours
      const hoursMin = Number(state.filters.hour_range.min);
      const hoursMax = Number(state.filters.hour_range.max);

      const hoursFiltered = wpData.filter(
        (data) =>
          Number(data?.meta?.hp_hours) >= hoursMin &&
          Number(data?.meta?.hp_hours) <= hoursMax
      );
      wpData = hoursFiltered;

      // Filter by Length
      const lengthMin = Number(state.filters.length_range.min);
      const lengthMax = Number(state.filters.length_range.max);

      const lengthFiltered = wpData.filter(
        (data) =>
          Number(data?.meta?.hp_v_length) >= lengthMin &&
          Number(data?.meta?.hp_v_length) <= lengthMax
      );
      wpData = lengthFiltered;

      // Filter by Exterior Color
      if (state.filters.extColors.length > 0) {
        const extColorFiltered = wpData.filter((data) =>
          state.filters.extColors
            .toString()
            .includes(data?.hp_listing_v_details_exterior_co[0]?.name)
        );
        wpData = extColorFiltered;
      }

      // Filter by Interior Color
      if (state.filters.intColors.length > 0) {
        const intColorFiltered = wpData.filter((data) =>
          state.filters.intColors
            .toString()
            .includes(data?.hp_listing_v_details_interior_co[0]?.name)
        );
        wpData = intColorFiltered;
      }

      // Filter by Seller
      if (state.filters.seller !== null) {
        let sellerFiltered = [];
        if (state.filters.seller) {
          sellerFiltered = wpData.filter(
            (data) =>
              data?.hp_listing_v_seller_seller_type[0]?.name === "Individual"
          );
        } else {
          sellerFiltered = wpData;
        }
        wpData = sellerFiltered;
      }

      // Filter by Title
      if (state.filters.cleanTitle !== null) {
        let titleFiltered = [];
        if (state.filters.cleanTitle) {
          titleFiltered = wpData.filter(
            (data) =>
              data?.hp_listing_v_features_title_stat[0]?.name === "Clean"
          );
        } else {
          titleFiltered = wpData;
        }
        wpData = titleFiltered;
      }

      // Final Data
      state.data = [...state.data, ...wpData];
    });
    builder.addCase(fetchWpCars.rejected, (state, action) => {
      state.isLoading = false;
      state.sorted_data = [];
      state.originalData = [];
      console.log(action.error);
    });

    // FETCH WP CAR DETAILS
    builder.addCase(fetchWpCarDetails.pending, (state, action) => {
      state.isLoading = true;
    });

    builder.addCase(fetchWpCarDetails.fulfilled, (state, action) => {
      state.isLoading = false;
      state.carDetails = action.payload.data;
    });

    builder.addCase(fetchWpCarDetails.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
    });

    // FETCH WP VENDORS
    builder.addCase(fetchWpVendors.pending, (state, action) => {
      state.isLoading = true;
    });

    builder.addCase(fetchWpVendors.fulfilled, (state, action) => {
      state.isLoading = false;
      state.vendorDetails = action.payload.data;
    });

    builder.addCase(fetchWpVendors.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
    });
  },
});

export const watercraftsReducer = watercrafts.reducer;
export const {
  setUser,
  changeLocation,
  changeRadius,
  changePrice,
  changeYear,
  changeHours,
  changeLength,
  changeCondition,
  changeExtColors,
  changeIntColors,
  changeCleanTitle,
  changeSeller,
  flatData,
  setSortBy,
  setSearchTerm,
  setDataBySearch,
  selectFilteredItems,
  activateFilter,
  resetFilter,
  resetSortedData,
  reset,
} = watercrafts.actions;
